import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, ShieldCheck, Eye, EyeOff, Home } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Register = () => {
    const navigate = useNavigate();
    const { register, verifyOtp, resendOtp } = useAuth();
    const [step, setStep] = useState(1); // 1: Register, 2: OTP
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
    });
    const [otp, setOtp] = useState('');
    const [resendTimer, setResendTimer] = useState(0);

    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateRegisterForm = () => {
        const { name, email, phone, password } = formData;
        if (!name || !email || !password) {
            toast.error('All required fields must be filled');
            return false;
        }
        const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
        if (!emailRegex.test(email)) {
            toast.error('Please enter a valid email address');
            return false;
        }
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return false;
        }
        return true;
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();

        if (!validateRegisterForm()) return;

        setLoading(true);
        try {
            const res = await register(formData);
            toast.success(res?.message || 'OTP sent to your email!');
            setStep(2);
            setResendTimer(40);
        } catch (error) {
            console.error('Registration error:', error);
            const message = error.response?.data?.message || 'Registration failed. Please try again.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            return toast.error('Please enter a valid 6-digit OTP');
        }
        setLoading(true);
        try {
            await verifyOtp({ email: formData.email, otp });
            toast.success('Email verified and logged in successfully!');
            navigate('/dashboard');
        } catch (error) {
            console.error('OTP verification error:', error);
            const message = error.response?.data?.message || 'Invalid or expired OTP';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setLoading(true);
        try {
            await resendOtp(formData.email);
            toast.success('New OTP sent to your email!');
            setResendTimer(40); // Restart timer
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to resend OTP');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white relative flex items-center justify-center px-4 overflow-hidden py-24">
            <SEO title="Create Account" description="Join the MehareTech ecosystem." />

            {/* Background Aesthetic */}
            <div className="absolute inset-0 z-0">
                <div className="absolute top-1/4 -right-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px]" />
                <div className="absolute bottom-1/4 -left-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[120px]" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-[480px] relative z-10"
            >
                <div className="bg-white/80 backdrop-blur-2xl p-8 sm:p-12 rounded-[2.5rem] border border-slate-200 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] relative overflow-hidden group">
                    {/* Inner Decorative Glow */}
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors duration-700" />

                    <div className="relative z-10">
                        <AnimatePresence mode="wait">
                            {step === 1 ? (
                                <motion.div
                                    key="register"
                                    initial={{ x: -20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: 20, opacity: 0 }}
                                >
                                    {/* Back to Home Button */}
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="mb-8"
                                    >
                                        <Link
                                            to="/"
                                            className="inline-flex items-center gap-2 text-slate-400 hover:text-primary transition-all duration-300 group/home"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover/home:bg-primary/10 group-hover/home:scale-110 transition-all">
                                                <Home size={14} className="group-hover/home:text-primary transition-colors" />
                                            </div>
                                            <span className="font-black text-[10px] uppercase tracking-[0.2em]">Return Home</span>
                                        </Link>
                                    </motion.div>

                                    <div className="mb-10">
                                        <h1 className="text-3xl font-black text-slate-900 mb-2 tracking-tight">Create <span className="text-gradient">Account.</span></h1>
                                        <p className="text-slate-500 text-sm font-medium">Join our global ecosystem today</p>
                                    </div>

                                    <form onSubmit={handleRegisterSubmit} className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 ml-1">Identity Name</label>
                                            <div className="relative group/input">
                                                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-focus-within/input:bg-primary/10 group-focus-within/input:text-primary transition-all">
                                                    <User size={16} />
                                                </div>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    required
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    placeholder="Full Name"
                                                    className="w-full pl-16 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 rounded-2xl focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-slate-900 font-semibold text-sm placeholder:text-slate-300"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 ml-1">Email Channel</label>
                                            <div className="relative group/input">
                                                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-focus-within/input:bg-primary/10 group-focus-within/input:text-primary transition-all">
                                                    <Mail size={16} />
                                                </div>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    required
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    placeholder="hello@company.com"
                                                    className="w-full pl-16 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 rounded-2xl focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-slate-900 font-semibold text-sm placeholder:text-slate-300"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 ml-1">Secure Password</label>
                                            <div className="relative group/input">
                                                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-focus-within/input:bg-primary/10 group-focus-within/input:text-primary transition-all">
                                                    <Lock size={16} />
                                                </div>
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    required
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    placeholder="Minimum 6 characters"
                                                    className="w-full pl-16 pr-12 py-3.5 bg-slate-50/50 border border-slate-200 rounded-2xl focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-slate-900 font-semibold text-sm placeholder:text-slate-300"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-primary transition-colors p-1"
                                                >
                                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full py-4 bg-slate-900 text-white font-black rounded-2xl shadow-xl shadow-slate-200 hover:bg-slate-800 active:scale-[0.98] transition-all flex items-center justify-center gap-3 group disabled:opacity-50 text-xs uppercase tracking-[0.2em] mt-4"
                                        >
                                            {loading ? 'Initializing...' : 'Register Account'}
                                            <ShieldCheck size={14} className="group-hover:scale-110 transition-transform" />
                                        </button>
                                    </form>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="otp"
                                    initial={{ x: -20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: 20, opacity: 0 }}
                                >
                                    <div className="text-center mb-10">
                                        <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                                            <Mail size={32} />
                                        </div>
                                        <h1 className="text-3xl font-black text-slate-900 mb-2 tracking-tight">Verify <span className="text-gradient">Email.</span></h1>
                                        <p className="text-slate-500 text-sm font-medium">Authentication code sent to <span className="text-slate-900 font-bold">{formData.email}</span></p>
                                    </div>

                                    <form onSubmit={handleOtpSubmit} className="space-y-6">
                                        <div className="space-y-2 text-center">
                                            <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Security Code</label>
                                            <input
                                                type="text"
                                                required
                                                maxLength="6"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value)}
                                                placeholder="000 000"
                                                className="w-full px-4 py-5 text-center text-3xl tracking-[0.3em] font-black bg-slate-50/50 border border-slate-200 rounded-2xl focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-slate-900"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full py-4 bg-slate-900 text-white font-black rounded-2xl shadow-xl shadow-slate-200 hover:bg-slate-800 active:scale-[0.98] transition-all flex items-center justify-center gap-2 group disabled:opacity-50 text-xs uppercase tracking-[0.2em]"
                                        >
                                            {loading ? 'Verifying...' : 'Complete Verification'}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleResendOtp}
                                            disabled={resendTimer > 0 || loading}
                                            className="w-full py-2 text-primary font-black uppercase tracking-widest text-[10px] hover:underline underline-offset-4 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                        >
                                            {resendTimer > 0 ? `Resend Available in ${resendTimer}s` : 'Request New Code'}
                                        </button>
                                    </form>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="mt-10 pt-8 border-t border-slate-100 text-center">
                            <p className="text-slate-400 text-[11px] font-semibold uppercase tracking-wider">
                                Already Registered? <Link to="/login" className="text-primary font-black hover:underline underline-offset-4 ml-1">Login Instead</Link>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer Subtle Text */}
                <p className="text-center mt-8 text-slate-300 text-[9px] font-black uppercase tracking-[0.4em]">© 2024 MehareTech Solutions</p>
            </motion.div>
        </div>
    );
};

export default Register;
